import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import elevateSearch from '@components/header/search/methods/elevateSearch';
import { getLiveSuggestions, getFallbackSuggestions } from '@components/header/search/methods/getSuggestions';
import useSessionStore from '@services/stores/SessionStore';
import useApplicationStore from '@services/stores/ApplicationStore';
import Router from 'next/router';
import React from 'react';
import { getTranslation } from '@services/translations';

interface SearchStoreState {
    query: string;
    dirty: boolean;
    products: any[];
    content: any[];
    suggestions: any[];
    hitCount: number;
    suggestedItems: any[];
    searchInputRef?: React.RefObject<HTMLInputElement>;

    setQuery: (q: string) => void;
    loadSuggestions: () => Promise<void>;
    onFocus: (actions?: any, t?: (key: string) => string) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    goToSearchResults: () => void;
    clearSearch: () => void;
    setSearchInputRef: (ref: React.RefObject<HTMLInputElement>) => void;
    getFallbackSuggestions: typeof getFallbackSuggestions;
}

let inputTimeout: ReturnType<typeof setTimeout>;
let focusTimeout: ReturnType<typeof setTimeout>;

function safeStringify(obj: any) {
    const seen = new WeakSet();
    return JSON.stringify(obj, (key, value) => {
        if (typeof value === "object" && value !== null) {
            if (seen.has(value)) return; // skip circular reference
            seen.add(value);
        }
        return value;
    });
}

export const useSearchStore = create<SearchStoreState>()(
        (set, get) => ({
            query: '',
            dirty: false,
            products: [],
            content: [],
            suggestions: [],
            hitCount: 0,
            suggestedItems: [],
            searchInputRef: undefined,

            setQuery: (q) => {
                set({ query: q });
                clearTimeout(inputTimeout);

                if (q.length > 2) {
                    set({ dirty: true });
                    const { config, salesChannel } = useSessionStore.getState().session;
                    const isMobile = useApplicationStore.getState().isMobile;

                    inputTimeout = setTimeout(() => {
                        elevateSearch(q, config, isMobile, get(), set);
                    }, 200);
                } else {
                    set({
                        dirty: false,
                        products: [],
                        content: [],
                        suggestions: [],
                        hitCount: 0
                    });
                }
            },

            loadSuggestions: async () => {
                const { salesChannel } = useSessionStore.getState().session;
                const res = await getLiveSuggestions(salesChannel);
                set({ suggestedItems: res });
            },

            onFocus: (actions, t) => {
                const focus = useApplicationStore.getState().modals.searchOpen;
                const searchInputRef = get().searchInputRef;

                if (focus) {
                    actions?.click?.({
                        type: 'open_search',
                        text: t?.('search') || 'search',
                        identifier: Router.asPath
                    });
                    clearTimeout(focusTimeout);
                    focusTimeout = setTimeout(() => {
                        searchInputRef?.current?.focus();
                    }, 150);
                } else {
                    searchInputRef?.current?.blur();
                }
            },

            onKeyDown: (e) => {
                if (e.key === 'Enter') {
                    get().goToSearchResults();
                }
            },

            goToSearchResults: () => {
                const { query } = get();
                const focus = useApplicationStore.getState().modals.searchOpen;
                const toggleDrawer = useApplicationStore.getState().methods.toggleDrawer;

                if (query.length > 2 && focus) {
                    Router.push({
                        pathname: getTranslation('search-url', 'general'),
                        query: { q: query }
                    });
                } else {
                    toggleDrawer('searchOpen');
                }
            },

            clearSearch: () => {
                const toggleDrawer = useApplicationStore.getState().methods.toggleDrawer;
                const searchInputRef = get().searchInputRef;
                set({ query: '' });
                searchInputRef?.current?.focus();
                toggleDrawer('searchOpen');
            },

            setSearchInputRef: (ref) => set({ searchInputRef: ref }),
            getFallbackSuggestions
        }),
);